/* eslint-disable @typescript-eslint/no-explicit-any */
import { groupBy } from './CommonUtils';
import {
  ALPHA_FRONT_IMAGE_URL,
  ALPHA_BACK_IMAGE_URL,
  FP_APP_URL,
  CORP_APP_URL,
  API_SERVER_URL,
} from '@/constants/Config';
import type {
  IApparel,
  IColor,
  IProductOption,
  IProductSelectionsResponse,
  IProductVariantSelections,
} from '@/common/interfaces/apparel.interface';
import type { IVariant } from '@/common/interfaces/shopify.interface';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { Product } from '@/utilities/ProductGalleryUtils';
import { ACCOUNT_TYPES } from '@/constants/Home';
import type {
  IProductVariant,
  IRevisionData,
  IVariantSnapshot,
} from '@/common/interfaces/design.interface';

/**
 * @deprecated - Use getApparelImages instead.
 * Function to fetch the images of apparel based on style code and color code
 *
 * @param styleCode - the style code of the apparel
 * @param colorCode - the color code of the apparel
 */
export const getAlphaApparelImages = (styleCode: string, colorCode: string) => {
  let apparelImages = [];

  apparelImages = [
    `${ALPHA_FRONT_IMAGE_URL + styleCode.toLowerCase()}_${colorCode.toLowerCase()}_p.jpg`,
    `${ALPHA_BACK_IMAGE_URL + styleCode.toLowerCase()}_bk_${colorCode.toLowerCase()}_p.jpg`,
  ];

  return apparelImages;
};

/**
 * Function to fetch the images of apparel based on style code and hex code using the new API
 *
 * @param styleCode - the style code of the apparel
 * @param hexCode - the hex code of the apparel (without #)
 */
export const getApparelImages = (styleCode: string, hexCode: string) => {
  // Remove # from hexCode if present
  const cleanHexCode = hexCode.replace('#', '');

  const apparelImages = [
    `${API_SERVER_URL}api/apparel-image/${styleCode.toLowerCase()}/${cleanHexCode.toLowerCase()}/front`,
    `${API_SERVER_URL}api/apparel-image/${styleCode.toLowerCase()}/${cleanHexCode.toLowerCase()}/back`,
  ];

  return apparelImages;
};

/**
 * Function that formats color name with casing.
 *
 * @param {string} colorName
 */
const formatColorName = (colorName: string) =>
  colorName
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Function that parses colors into readable format.
 *
 * @param {string} colors
 */
export const parseColors = (colors: string) => {
  if (!colors) {
    return 'N/A';
  }

  return colors
    .split(';')
    .map((color: string) => {
      const colorList = color.split(':');

      if (colorList.length === 1) {
        return {
          cc: 'N/A',
          hex: 'N/A',
          name: colorList[0],
        };
      }

      return {
        cc: colorList[0],
        hex: `#${colorList[1]}`,
        name: formatColorName(colorList[2]),
      };
    })
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
};

/**
 * Method to get the apparel information of the design.
 *
 * @param design - The order item design.
 * @param designColor - The design color
 */
export const getApparelInformation = (
  design: {
    apparel: IApparel | null;
    external_apparel: IApparel | null;
    has_external_apparel: boolean;
  },
  designColor: string
) => {
  /** If the design is not an external apparel. */
  if (!design.has_external_apparel) {
    const apparelColor = parseColors(designColor)[0];

    if (typeof apparelColor === 'string') {
      return {
        styleCode: design.apparel ? design.apparel.style_code : '',
        brand: design.apparel ? design.apparel.brand : '',
        hexCode: '',
        color: apparelColor,
        isExternal: false,
      };
    }

    return {
      styleCode: design.apparel ? design.apparel.style_code : '',
      brand: design.apparel ? design.apparel.brand : '',
      hexCode: apparelColor ? apparelColor.hex : '',
      color: apparelColor ? apparelColor.name : '',
      isExternal: false,
    };
  }

  /**
   * If the design is external apparel
   */
  if (design.has_external_apparel) {
    return {
      styleCode: design.external_apparel ? design.external_apparel.style_code : '',
      brand: design.external_apparel ? design.external_apparel.brand : '',
      name: design.external_apparel ? design.external_apparel.name : '',
      color: design.external_apparel ? design.external_apparel.color : '',
      hexCode: design.external_apparel ? design.external_apparel.hex : '',
      isExternal: true,
    };
  }

  return {
    styleCode: '',
    brand: '',
    name: '',
    color: '',
    hexCode: '',
    isExternal: false,
  };
};

/**
 * Method to return the color in the format of "CC:000000:Black"
 *
 * @param hexCode - hex code
 * @param colorCode - color code
 * @param color - color
 */
export const getApparelColor = (colorCode: string, hexCode: string, color: string) => {
  const hexCodeWithoutHash = hexCode.replace('#', '');

  if (colorCode) {
    return `${colorCode}:${hexCodeWithoutHash}:${color}`;
  }

  return `SHOPIFYCC:${hexCodeWithoutHash}:${color}`;
};

/**
 * Method to check if the color is MTO or not.
 *
 * @param name - Color name.
 */
export const checkMtoColor = (name: string) => {
  if (name.toLowerCase().indexOf('mto') !== -1) {
    return true;
  }

  return false;
};

export const colorNameIncludesMto = (name?: string | null): boolean => {
  if (!name) return false;
  const value = String(name);
  if (!value.includes(':')) return checkMtoColor(value);
  const parts = value.split(':');
  const colorName = parts.length >= 3 ? parts[2] : parts[parts.length - 1];
  return checkMtoColor(colorName || '');
};

/**
 * Method to replace MTO from the color name.
 *
 * @param name - Color name.
 */
export const replaceMto = (name: string) => {
  if (name) {
    return name.replace(/mto/gi, '').trim();
  }

  return name;
};

/**
 * Method to return the product url.
 *
 * @param styleCode - Apparel style code.
 * @param hexCode - Color hex code.
 */
export const getShopifyProductUrl = (styleCode: string, hexCode: string, userType?: string) => {
  const URL = userType && userType === ACCOUNT_TYPES.CORPORATE ? CORP_APP_URL : FP_APP_URL;

  if (hexCode) {
    return `${URL}product?styleCode=${styleCode}&hex=${hexCode}`;
  }

  return `${URL}product?styleCode=${styleCode}`;
};

/**
 * Resolves the front-facing image URL for a product variant.
 *
 * @param variant -
 *   array of objects with optional `imgUrl` and `location` fields.
 * @returns {string} `variant.imgUrl` when present; otherwise the `imgUrl` of the
 *   image whose `location` is `"front"` (case-insensitive); otherwise the first
 *   image's `imgUrl`; or an empty string when no image is available.
 */
export const getVariantFrontImage = (variant: IVariant) => {
  if (variant.imgUrl) return variant.imgUrl;

  const images = variant.images ?? [];
  if (!images.length) return '';

  const frontImage = images.find((image) => (image.location ?? '').toLowerCase() === 'front');
  return frontImage?.imgUrl || images[0]?.imgUrl || '';
};

/**
 * Method to modify the shopify variants into the colors list.
 *
 * @param variants - Color variants.
 */
export const getShopifyProductVariants = (variants: IVariant[]) => {
  const colorVariants: IColor[] = [];

  const hexCodeList = groupBy(variants, 'code');

  Object.keys(hexCodeList).forEach((hexCode: string) => {
    const sortedVariantsArray = hexCodeList[hexCode].sort((a: IVariant, b: IVariant) => {
      const order: { [key: string]: number } = { front: 1, back: 2 };
      return (order[a.location.toLowerCase()] || 3) - (order[b.location.toLowerCase()] || 3);
    });

    const variant = sortedVariantsArray[0];
    let isMtoColor = false;
    let colorName = '';
    let tooltipName = 'Standard Color';

    if (variant.name.toLowerCase().indexOf('mto') !== -1) {
      isMtoColor = true;
      colorName = variant.name.replace(/mto/gi, '').trim();
      tooltipName = 'Made-to-Order Color';
    } else {
      isMtoColor = false;
      tooltipName = 'Standard Color';
      colorName = variant.name;
    }

    colorVariants.push({
      hex: variant.code,
      name: colorName,
      cc: 'SHOPIFYCC',
      isMtoColor,
      tooltipName,
      imgUrl: getVariantFrontImage(sortedVariantsArray[0]),
    });
  });

  return colorVariants;
};

/**
 * Method to generate the result that will be used for the revision request.
 *
 * @param shopifyResult - The shopify result.
 */
export const getProductDetailsFromShopifyResult = (shopifyResult: Product) => {
  const selectedShopifyResult = {
    name: '',
    color: { hexCode: '', name: '', isMtoColor: false, cc: 'SHOPIFYCC' },
    imgUrl: '',
    styleCode: '',
    productUrl: '',
    colorVariants: [] as IColor[],
  };

  if (shopifyResult) {
    selectedShopifyResult.name = shopifyResult.name || shopifyResult.brand.name;
    selectedShopifyResult.color.hexCode = shopifyResult.color.code;
    selectedShopifyResult.color.name = shopifyResult.color.name
      ? (shopifyResult.color.name as string)
      : '';
    selectedShopifyResult.color.isMtoColor = shopifyResult.color.name
      ? checkMtoColor(shopifyResult.color.name)
      : false;
    selectedShopifyResult.imgUrl = shopifyResult.imgUrl;
    selectedShopifyResult.styleCode = shopifyResult.styleCode;
    selectedShopifyResult.productUrl = getShopifyProductUrl(
      shopifyResult.styleCode,
      shopifyResult.color.code
    );
    selectedShopifyResult.colorVariants = getShopifyProductVariants(
      shopifyResult.variants ?? []
    ) as IColor[];
  }

  return selectedShopifyResult;
};

/**
 * Method to retrieve the color name from the color schema.
 *
 * @param color - Color schema.
 */
export const getColorName = (color: string) => {
  if (color) {
    const splittedColor = color.split(':');

    return splittedColor && splittedColor.length
      ? splittedColor[2].replace(/mto/gi, '').trim()
      : color;
  }

  return 'N/A';
};

/**
 * Updates the apparel details based on the revision data and apparel info.
 *
 * @param {IRevisionData} revisionData - The revision data containing shopify product ID and hex code.
 * @param {IApparel} apparel - The current apparel details.
 * @param {Function} getProductInfo - The function to get the product info.
 * @return {Promise<IApparel>} The updated apparel details.
 */
export const updateApparelDetails = async (
  revisionData: IRevisionData,
  apparel: IApparel,
  getProductInfo: any
): Promise<IApparel> => {
  try {
    const productRes = await getProductInfo({
      id: revisionData.shopifyProductId,
    });

    if (productRes && 'data' in productRes) {
      const { product } = productRes.data;
      const selectedVariant = product.variants.find(
        (variant: IProductVariant) => variant.color.code === revisionData.apprelHexCode
      );

      const SELECTED_APPAREL = {
        isExternal: !product.type,
        weight: product.weight ? parseFloat(product.weight) : 0,
        type: product.type,
        price: product.price || (selectedVariant ? selectedVariant.price : 0) || 0,
        color: replaceMto(revisionData.apparelColorName),
        category: product.categoryForQuoter,
        hexCode: revisionData.apprelHexCode,
        colorCode: revisionData.apparelColorCode,
        brand: product.brand ? product.brand.name : '',
        styleCode: product.styleCode ? product.styleCode : revisionData.apparelStyleCode,
        sizes: selectedVariant
          ? selectedVariant.sizes.map((size: { name: string }) => `0:${size.name}`).join(';')
          : null,
      };

      const UPDATED_APPAREL = {
        ...apparel,
        ...SELECTED_APPAREL,
      };

      return UPDATED_APPAREL;
    }
  } catch (err) {
    return apparel;
  }

  return apparel;
};

export function getUnavailableOptionValues(
  options: IProductOption[],
  variants: IProductVariantSelections[],
  selections: Record<string, string>
): Record<string, string[]> {
  return options.reduce<Record<string, string[]>>((unavailableOptionValues, option) => {
    unavailableOptionValues[option.key] = option.values
      .filter((optionValue) => {
        const testSelections = {
          ...selections,
          [option.key]: optionValue.value,
        };

        const hasMatchingVariant = variants.some((variant) =>
          Object.entries(testSelections).every(([key, value]) => variant.selections[key] === value)
        );

        return !hasMatchingVariant;
      })
      .map((optionValue) => optionValue.value);

    return unavailableOptionValues;
  }, {});
}

export function findMatchingVariant(
  variants: IProductVariantSelections[],
  selections: Record<string, string>
): IProductVariantSelections | undefined {
  return variants.find((variant) =>
    Object.entries(selections).every(([key, value]) => variant.selections[key] === value)
  );
}

export function getDefaultVariant(
  variants: IProductVariantSelections[]
): IProductVariantSelections | undefined {
  return variants.find((variant) => variant.default) ?? variants[0];
}

export function mapColorOptionsToIColor(options: IProductOption[]): IColor[] {
  const colorOption = options.find((option) => option.renderType === 'color_selection');

  if (!colorOption) {
    return [];
  }

  return colorOption.values
    .filter((optionValue) => optionValue.hexcode)
    .map((optionValue) => {
      const isMtoColor = optionValue.type === 'mto' || checkMtoColor(optionValue.value);
      const colorName = replaceMto(optionValue.value);

      return {
        hex: optionValue.hexcode as string,
        name: colorName,
        cc: optionValue.value,
        isMtoColor,
        tooltipName: isMtoColor ? 'Made-to-Order Color' : 'Standard Color',
      };
    });
}

export interface IResolveInitialPromoSelectionsOptions {
  apparelColorName?: string;
  variantSnapshot?: IVariantSnapshot;
}

export function parseVariantSnapshotSelections(
  variantsJson: string | Record<string, string> | null | undefined
): Record<string, string> | null {
  if (!variantsJson) {
    return null;
  }

  const isNonEmptyRecord = (value: Record<string, string>): boolean =>
    typeof value === 'object' && Object.keys(value).length > 0;

  if (typeof variantsJson === 'object') {
    return isNonEmptyRecord(variantsJson) ? variantsJson : null;
  }

  try {
    const parsed = JSON.parse(variantsJson) as Record<string, string>;

    if (parsed && isNonEmptyRecord(parsed)) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

export function findVariantBySku(
  variants: IProductVariantSelections[],
  sku: string
): IProductVariantSelections | undefined {
  if (!sku) {
    return undefined;
  }

  return variants.find((variant) => variant.sku === sku);
}

export function shouldUsePromoApparelImages(variantSnapshot?: IVariantSnapshot): boolean {
  return Boolean(variantSnapshot?.sku);
}

export function getPromoVariantImages(variant: IProductVariantSelections): {
  frontImage: string;
  backImage: string;
} {
  const images = variant.images ?? [];

  if (!images.length) {
    return { frontImage: '', backImage: '' };
  }

  const frontImage =
    images.find((image) => (image.location ?? '').toLowerCase() === 'front')?.url ?? '';
  const backImage =
    images.find((image) => (image.location ?? '').toLowerCase() === 'back')?.url ?? '';

  return {
    frontImage: frontImage || images[0]?.url || '',
    backImage: backImage || images[1]?.url || images[0]?.url || '',
  };
}

export function getPromoApparelImagesBySku(
  productSelections: IProductSelectionsResponse,
  sku: string
): { frontImage: string; backImage: string } {
  const variant = findVariantBySku(productSelections.variants, sku);

  if (!variant) {
    return { frontImage: '', backImage: '' };
  }

  return getPromoVariantImages(variant);
}

function resolveVariantFromColor(
  options: IProductOption[],
  variants: IProductVariantSelections[],
  apparelColorName: string
): IProductVariantSelections | undefined {
  const defaultVariant = getDefaultVariant(variants);
  const colorOption = options.find((option) => option.renderType === 'color_selection');

  if (!defaultVariant || !colorOption) {
    return undefined;
  }

  const tempSelections = {
    ...defaultVariant.selections,
    [colorOption.key]: apparelColorName,
  };

  return findMatchingVariant(variants, tempSelections) ?? defaultVariant;
}

export function resolveInitialPromoSelections(
  productSelections: IProductSelectionsResponse,
  options: IResolveInitialPromoSelectionsOptions = {}
): { selections: Record<string, string>; sku: string } {
  const { variants, options: productOptions } = productSelections;
  const { apparelColorName, variantSnapshot } = options;

  if (variantSnapshot?.sku) {
    const skuMatchVariant = findVariantBySku(variants, variantSnapshot.sku);

    if (skuMatchVariant) {
      return {
        selections: { ...skuMatchVariant.selections },
        sku: skuMatchVariant.sku,
      };
    }
  }

  if (variantSnapshot?.variants_json) {
    const snapshotSelections = parseVariantSnapshotSelections(variantSnapshot.variants_json);

    if (snapshotSelections) {
      const snapshotMatchVariant = findMatchingVariant(variants, snapshotSelections);

      if (snapshotMatchVariant) {
        return {
          selections: { ...snapshotMatchVariant.selections },
          sku: snapshotMatchVariant.sku,
        };
      }
    }
  }

  if (apparelColorName) {
    const colorMatchVariant = resolveVariantFromColor(productOptions, variants, apparelColorName);

    if (colorMatchVariant) {
      return {
        selections: { ...colorMatchVariant.selections },
        sku: colorMatchVariant.sku,
      };
    }
  }

  const defaultVariant = getDefaultVariant(variants);

  if (defaultVariant) {
    return {
      selections: { ...defaultVariant.selections },
      sku: defaultVariant.sku,
    };
  }

  return { selections: {}, sku: '' };
}

export function getPromoColorFromSelections(
  productSelections: IProductSelectionsResponse,
  selections: Record<string, string>
): { colorName: string; hexCode: string } {
  const colorOption = productSelections.options.find(
    (option) => option.renderType === 'color_selection'
  );

  if (!colorOption) {
    return { colorName: '', hexCode: '' };
  }

  const selectedColorValue = selections[colorOption.key];
  const matchedOptionValue = colorOption.values.find(
    (optionValue) => optionValue.value === selectedColorValue
  );

  return {
    colorName: selectedColorValue ?? '',
    hexCode: matchedOptionValue?.hexcode ?? '',
  };
}

export function normalizeProductSelectionsResponse(
  response: unknown
): IProductSelectionsResponse | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const candidate = response as Record<string, unknown>;

  if ('options' in candidate && 'variants' in candidate) {
    if (Array.isArray(candidate.options) && Array.isArray(candidate.variants)) {
      return candidate as unknown as IProductSelectionsResponse;
    }

    return null;
  }

  if (candidate.data && typeof candidate.data === 'object') {
    return normalizeProductSelectionsResponse(candidate.data);
  }

  return null;
}

export function isPromoProductSelections(
  productSelections: IProductSelectionsResponse | null | undefined
): boolean {
  if (!productSelections) {
    return false;
  }

  if (productSelections.isModernProduct) {
    return true;
  }

  return (
    productSelections.options.length > 1 ||
    productSelections.options.some((option) => option.renderType === 'single_selection')
  );
}

export const isPromoCartItem = (cartItem?: Pick<ICartItemDetail, 'variantSnapshot'>): boolean =>
  Boolean(cartItem?.variantSnapshot);

export const getPromoProductQuoterFields = (
  variantSnapshot?: IVariantSnapshot
): { sku: string; isModernProduct: true } | Record<string, never> => {
  if (!variantSnapshot?.sku) {
    return {};
  }

  return {
    sku: variantSnapshot.sku,
    isModernProduct: true,
  };
};
